import { useCallback } from "react";

import { drawersActions, useDispatchDrawers, useIsDrawerOpen } from "contexts/drawers";
import { usePanelMode } from "contexts/panelMode";
import { sidePanelsActions, useDispatchSidePanels, useIsPanelOpen } from "contexts/sidePanels";

/**
 * Hook that provides a unified panel toggle function.
 * Automatically uses the correct panel type based on screen aspect ratio.
 *
 * @returns Object with:
 *  - togglePanel: Function to toggle a panel by ID
 *  - openPanel: Function to open a panel by ID
 *  - closePanel: Function to close a panel by ID
 *  - mode: Current panel mode ("sidePanel" or "drawer")
 */
export function useUnifiedPanelToggle() {
    const mode = usePanelMode();
    const dispatchSidePanels = useDispatchSidePanels();
    const dispatchDrawers = useDispatchDrawers();

    const togglePanel = useCallback(
        (panelId: string) => {
            if (mode === "sidePanel") {
                dispatchSidePanels(sidePanelsActions.togglePanel(panelId));
            } else {
                dispatchDrawers(drawersActions.toggleDrawer(panelId));
            }
        },
        [mode, dispatchSidePanels, dispatchDrawers],
    );

    const openPanel = useCallback(
        (panelId: string) => {
            if (mode === "sidePanel") {
                dispatchSidePanels(sidePanelsActions.openPanel(panelId));
            } else {
                dispatchDrawers(drawersActions.openDrawer(panelId));
            }
        },
        [mode, dispatchSidePanels, dispatchDrawers],
    );

    const closePanel = useCallback(
        (panelId: string) => {
            if (mode === "sidePanel") {
                dispatchSidePanels(sidePanelsActions.closePanel(panelId));
            } else {
                dispatchDrawers(drawersActions.closeDrawer());
            }
        },
        [mode, dispatchSidePanels, dispatchDrawers],
    );

    const closeAllPanels = useCallback(() => {
        if (mode === "sidePanel") {
            dispatchSidePanels(sidePanelsActions.closeAllPanels());
        } else {
            dispatchDrawers(drawersActions.closeDrawer());
        }
    }, [mode, dispatchSidePanels, dispatchDrawers]);

    return {
        togglePanel,
        openPanel,
        closePanel,
        closeAllPanels,
        mode,
    };
}

/**
 * Hook to check if a specific panel is currently open (works for both modes)
 */
export function useIsPanelOpenUnified(panelId: string): boolean {
    const mode = usePanelMode();
    const isSidePanelOpen = useIsPanelOpen(panelId);
    const isDrawerOpen = useIsDrawerOpen(panelId);

    return mode === "sidePanel" ? isSidePanelOpen : isDrawerOpen;
}
